import { howDidYouKnowDuneEnum, positionsInTheCompanyEnum } from "@dune/types";
import { format } from "date-fns";

import { zodResolver } from "@hookform/resolvers/zod";
import { DateValue, parseDate } from "@internationalized/date";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
	fileOnSocietyTypes,
	humanProcessingStatuses,
	warrantyTypes,
} from "@dune/vars";
import { useGetBrokerDetailsSuspense } from "../../../api/queries/societies.queries";
import { StatusForCategory } from "../../../auth/register/register-second-step-broker/UseRegisterSecondStepBroker";

const categorySchema = z.object({
	category: z.string(),
	status: z.enum(["REGISTERED", "NOT_REGISTERED"]),
	registrationDate: z.string(),
	categoryDeletionDate: z.string().optional(),
	fundCollection: z.boolean(),
	mandants: z.string().optional(),
});

const booleanEnum = z.enum(["true", "false"]);

const accessibleProductSchema = z.object({
	name: z.string(),
	authorized: booleanEnum,
	commission: z.string(),
	minCommission: z.string(),
	maxCommission: z.string(),
	isReunionDepartmentAuthorized: z.boolean(),
	binderId: z.number().optional(),
	warrantyType: z.enum(warrantyTypes).optional(),
	gfaProduct: z.boolean().optional(),
});

const documentSchema = z.object({
	file: z.object({
		id: z.string(),
		name: z.string(),
		key: z.string(),
		mimeType: z.string(),
		bucket: z.string(),
		humanProcessingStatus: z.enum(humanProcessingStatuses),
	}),
	type: z.enum(fileOnSocietyTypes),
});

const partnershipRequestAnalysisSaveSchema = z.object({
	oriasNumber: z.string().optional(),
	siren: z.string().optional(),
	denomination: z.string().optional(),
	headquartersAddress: z
		.object({
			streetNumber: z.string().optional(),
			streetName: z.string().optional(),
			postalCode: z.string().optional(),
			city: z.string().optional(),
			address: z.string().optional(),
		})
		.optional(),
	categories: z.array(categorySchema).optional(),

	userId: z.number().optional(),
	societyId: z.number(),
	lastname: z.string().optional(),
	firstname: z.string().optional(),
	email: z.string().optional(),
	position: z.enum(positionsInTheCompanyEnum).optional(),
	landlinePhone: z.string().optional(),
	phone: z.string().optional(),
	isSignatory: booleanEnum.optional(),

	lastnameSignatory: z.string().optional(),
	firstnameSignatory: z.string().optional(),
	emailSignatory: z.string().optional(),
	positionSignatory: z.enum(positionsInTheCompanyEnum).optional(),
	landlinePhoneSignatory: z.string().optional(),
	phoneSignatory: z.string().optional(),

	iban: z.string().optional(),
	bic: z.string().optional(),

	insurer: z.string().optional(),
	validityStartDate: z.custom<DateValue>().optional(),
	validityEndDate: z.custom<DateValue>().optional(),

	accessibleProducts: z.array(accessibleProductSchema),

	howDidYouKnowDune: z.enum(howDidYouKnowDuneEnum).optional(),
	newsletter: z.boolean().optional(),

	companyDocuments: z.array(documentSchema).optional(),
	userDocuments: z.array(documentSchema).optional(),
	lcbftDocuments: z.array(documentSchema).optional(),
	bankDocuments: z.array(documentSchema).optional(),
	rcDocuments: z.array(documentSchema).optional(),
});

const partnershipRequestAnalysisFormSchema = z
	.object({
		oriasNumber: z.string().optional(),
		siren: z.string().optional(),
		denomination: z.string().optional(),
		headquartersAddress: z
			.object({
				streetNumber: z.string().optional(),
				streetName: z.string().optional(),
				postalCode: z.string().optional(),
				city: z.string().optional(),
				address: z.string().optional(),
			})
			.optional(),
		categories: z.array(categorySchema).optional(),

		userId: z.number().optional(),
		societyId: z.number(),
		lastname: z.string().min(1, "Le nom est requis"),
		firstname: z.string().min(1, "Le prénom est requis"),
		email: z.email("Email invalide").min(1, "L'email est requis"),
		position: z.enum(positionsInTheCompanyEnum, {
			error: "La fonction est requise",
		}),
		landlinePhone: z.string().optional(),
		phone: z.string().min(1, "Le mobile est requis"),
		isSignatory: booleanEnum,

		lastnameSignatory: z.string().optional(),
		firstnameSignatory: z.string().optional(),
		emailSignatory: z.string().optional(),
		positionSignatory: z.enum(positionsInTheCompanyEnum).optional(),
		landlinePhoneSignatory: z.string().optional(),
		phoneSignatory: z.string().optional(),

		iban: z.string().min(1, "IBAN requis"),
		bic: z.string().min(1, "BIC requis"),

		insurer: z.string().min(1, "Assureur requis"),
		validityStartDate: z.custom<DateValue>((val) => val != null, {
			message: "Date requise",
		}),
		validityEndDate: z.custom<DateValue>((val) => val != null, {
			message: "Date requise",
		}),

		accessibleProducts: z.array(accessibleProductSchema),

		howDidYouKnowDune: z.enum(howDidYouKnowDuneEnum, {
			error: "Ce champ est requis",
		}),
		newsletter: z.boolean().optional(),

		companyDocuments: z.array(documentSchema).optional(),
		userDocuments: z.array(documentSchema).optional(),
		lcbftDocuments: z.array(documentSchema).optional(),
		bankDocuments: z.array(documentSchema).optional(),
		rcDocuments: z.array(documentSchema).optional(),
	})

	.superRefine((data, ctx) => {
		// Validate userId is present
		if (!data.userId) {
			ctx.addIssue({
				code: "custom",
				message: "Aucun utilisateur associé à cette société",
				path: ["userId"],
			});
		}

		if (data.validityStartDate && data.validityEndDate) {
			try {
				const startDate = new Date(data.validityStartDate.toString());
				const endDate = new Date(data.validityEndDate.toString());

				if (startDate > endDate) {
					ctx.addIssue({
						code: "custom",
						message:
							"La date de fin de validité ne peut pas être antérieure à la date de début",
						path: ["validityStartDate"],
					});
				}
			} catch {
				ctx.addIssue({
					code: "custom",
					message: "Format de date invalide",
					path: ["validityStartDate"],
				});
			}
		}

		if (data.isSignatory === "false") {
			if (!data.lastnameSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "Le nom du signataire est requis",
					path: ["lastnameSignatory"],
				});
			}
			if (!data.firstnameSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "Le prénom du signataire est requis",
					path: ["firstnameSignatory"],
				});
			}
			if (!data.emailSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "L'email du signataire est requis",
					path: ["emailSignatory"],
				});
			}
			if (!data.positionSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "La fonction du signataire est requise",
					path: ["positionSignatory"],
				});
			}
			if (!data.phoneSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "Le téléphone du signataire est requis",
					path: ["phoneSignatory"],
				});
			}
		}
	});

export type PartnershipRequestAnalysisFormData = z.infer<
	typeof partnershipRequestAnalysisFormSchema
>;

export type PartnershipRequestAnalysisSaveData = z.infer<
	typeof partnershipRequestAnalysisSaveSchema
>;

type FormAccessibleProduct = z.infer<typeof accessibleProductSchema>;

const defaultProducts: FormAccessibleProduct[] = [
	{
		name: "DO - CARAVELA",
		commission: "15",
		minCommission: "450,00",
		maxCommission: "250000",
		authorized: "true",
		isReunionDepartmentAuthorized: false,
		gfaProduct: false,
		binderId: 1,
		warrantyType: "DO",
	},
	{
		name: "TRC - CARAVELA",
		commission: "15",
		minCommission: "0,00",
		maxCommission: "250000",
		authorized: "true",
		isReunionDepartmentAuthorized: false,
		gfaProduct: false,
		binderId: 1,
		warrantyType: "TRC",
	},
	{
		name: "MRT - ZURICH",
		commission: "15",
		minCommission: "0,00",
		maxCommission: "250000",
		authorized: "true",
		isReunionDepartmentAuthorized: false,
		gfaProduct: false,
		binderId: 2,
		warrantyType: "MRT",
	},
	{
		name: "GFA - CARAVELA",
		commission: "10",
		minCommission: "0,00",
		maxCommission: "250000",
		authorized: "true",
		isReunionDepartmentAuthorized: false,
		gfaProduct: true,
	},
	{
		name: "DO - AXERIA",
		commission: "15",
		minCommission: "0,00",
		maxCommission: "250000",
		authorized: "true",
		isReunionDepartmentAuthorized: false,
		gfaProduct: false,
		binderId: 3,
		warrantyType: "DO",
	},
	{
		name: "TRC - AXERIA",
		commission: "15",
		minCommission: "0,00",
		maxCommission: "250000",
		authorized: "true",
		isReunionDepartmentAuthorized: false,
		gfaProduct: false,
		binderId: 3,
		warrantyType: "TRC",
	},
	{
		name: "PJ - CFDP",
		commission: "15",
		minCommission: "0,00",
		maxCommission: "250000",
		authorized: "true",
		isReunionDepartmentAuthorized: false,
		gfaProduct: false,
		binderId: 5,
		warrantyType: "PJ",
	},
];

export function usePartnershipRequestAnalysis(id: string) {
	const form = useForm<PartnershipRequestAnalysisFormData>({
		resolver: zodResolver(partnershipRequestAnalysisFormSchema),
		defaultValues: {
			oriasNumber: "",
			siren: "",
			denomination: "",
			headquartersAddress: {
				streetNumber: "",
				streetName: "",
				postalCode: "",
				city: "",
				address: "",
			},
			categories: [],
			companyDocuments: [],

			lastname: "",
			firstname: "",
			email: "",
			position: undefined,
			landlinePhone: "",
			phone: "",
			isSignatory: "true",

			lastnameSignatory: "",
			firstnameSignatory: "",
			emailSignatory: "",
			positionSignatory: undefined,
			landlinePhoneSignatory: "",
			phoneSignatory: "",

			userDocuments: [],
			lcbftDocuments: [],

			iban: "",
			bic: "",
			bankDocuments: [],
			rcDocuments: [],

			insurer: "",
			validityStartDate: undefined,
			validityEndDate: undefined,

			accessibleProducts: defaultProducts,

			howDidYouKnowDune: "OTHER",
			newsletter: false,
		},
		mode: "onChange",
	});

	const { data: societyData } = useGetBrokerDetailsSuspense(id);
	const { setValue } = form;

	useEffect(() => {
		if (!societyData || !societyData.users || !societyData.categoriesOnSociety)
			return;

		const categories = societyData.categoriesOnSociety.map(
			(catOnSociety: {
				category: {
					category: string;
					status: StatusForCategory;
					registrationDate: Date | null;
					categoryDeletionDate?: Date | null;
					fundCollection: boolean;
				};
			}) => ({
				category: catOnSociety.category.category,
				status: catOnSociety.category.status,
				registrationDate: catOnSociety.category.registrationDate
					? new Date(catOnSociety.category.registrationDate).toISOString()
					: "",
				categoryDeletionDate: catOnSociety.category.categoryDeletionDate
					? new Date(catOnSociety.category.categoryDeletionDate).toISOString()
					: undefined,
				fundCollection: catOnSociety.category.fundCollection,
				mandants: "",
			}),
		);

		const productsFromDb = societyData?.brokerDetails?.accessibleProduct || [];

		const accessibleProducts = defaultProducts.map((defaultProduct) => {
			const found = productsFromDb.find((p) => p.name === defaultProduct.name);

			return {
				...defaultProduct,
				authorized:
					found?.authorized !== undefined
						? found.authorized
							? "true"
							: "false"
						: ("true" as z.infer<typeof booleanEnum>),
				commission: found?.commission ?? defaultProduct.commission,
				minCommission: found?.minCommission ?? defaultProduct.minCommission,
				maxCommission: found?.maxCommission ?? defaultProduct.maxCommission,
				isReunionDepartmentAuthorized:
					found?.isReunionDepartmentAuthorized ?? false,
				binderId: found?.binderId ?? defaultProduct.binderId,
				warrantyType: found?.warrantyType ?? defaultProduct.warrantyType,
				gfaProduct: found?.gfaProduct ?? defaultProduct.gfaProduct,
			};
		});

		const allFiles = societyData.files ?? [];

		const companyDocs = allFiles
			.filter((f) => f.type === "KBIS")
			.map((f) => ({
				file: {
					id: f.file.id,
					name: f.file.name,
					key: f.file.key,
					mimeType: f.file.mimeType,
					bucket: f.file.bucket,
					humanProcessingStatus: f.file.humanProcessingStatus,
				},
				type: f.type,
			}));
		const userDocs = allFiles
			.filter((f) => f.type === "IDENTITY")
			.map((f) => ({
				file: {
					id: f.file.id,
					name: f.file.name,
					key: f.file.key,
					mimeType: f.file.mimeType,
					bucket: f.file.bucket,
					humanProcessingStatus: f.file.humanProcessingStatus,
				},
				type: f.type,
			}));
		const rcDocuments = allFiles
			.filter((f) => f.type === "RC")
			.map((f) => ({
				file: {
					id: f.file.id,
					name: f.file.name,
					key: f.file.key,
					mimeType: f.file.mimeType,
					bucket: f.file.bucket,
					humanProcessingStatus: f.file.humanProcessingStatus,
				},
				type: f.type,
			}));
		const bankDocs = allFiles
			.filter((f) => f.type === "RIB")
			.map((f) => ({
				file: {
					id: f.file.id,
					name: f.file.name,
					key: f.file.key,
					mimeType: f.file.mimeType,
					bucket: f.file.bucket,
					humanProcessingStatus: f.file.humanProcessingStatus,
				},
				type: f.type,
			}));
		const lcbDocs = allFiles
			.filter((f) => f.type === "LCB")
			.map((f) => ({
				file: {
					id: f.file.id,
					name: f.file.name,
					key: f.file.key,
					mimeType: f.file.mimeType,
					bucket: f.file.bucket,
					humanProcessingStatus: f.file.humanProcessingStatus,
				},
				type: f.type,
			}));

		const mainUser =
			societyData.users.find((user) => !user.isSignatory) ||
			societyData.users[0];
		const signatoryUser = societyData.users.find((user) => user.isSignatory);

		setValue("oriasNumber", societyData.oriasNumber?.toString() || "");
		setValue("siren", societyData.siren || "");
		setValue("denomination", societyData.denomination || "");
		setValue("headquartersAddress", {
			streetNumber: societyData.headOfficeAddress?.streetNumber || "",
			streetName: societyData.headOfficeAddress?.streetName || "",
			postalCode: societyData.headOfficeAddress?.postalCode || "",
			city: societyData.headOfficeAddress?.city || "",
			address: "",
		});
		if (mainUser?.id) {
			setValue("userId", mainUser.id);
		}
		setValue("societyId", societyData.id);
		setValue("lastname", mainUser?.lastname ?? "");
		setValue("firstname", mainUser?.firstname ?? "");
		setValue("email", mainUser?.email ?? "");
		if (mainUser?.positionInTheCompany) {
			setValue("position", mainUser.positionInTheCompany);
		}
		setValue("landlinePhone", mainUser?.landlinePhone ?? "");
		setValue("phone", mainUser?.mobilePhone ?? "");
		setValue("isSignatory", mainUser?.isSignatory ? "true" : "false");
		setValue("newsletter", mainUser?.hasSubscribedToNewsletter ?? false);
		setValue(
			"howDidYouKnowDune",
			mainUser?.howDidYouKnowDune ?? howDidYouKnowDuneEnum.OTHER,
		);

		if (signatoryUser && signatoryUser.id !== mainUser?.id) {
			setValue("lastnameSignatory", signatoryUser.lastname ?? "");
			setValue("firstnameSignatory", signatoryUser.firstname ?? "");
			setValue("emailSignatory", signatoryUser.email ?? "");
			if (signatoryUser.positionInTheCompany) {
				setValue("positionSignatory", signatoryUser.positionInTheCompany);
			}
			setValue("landlinePhoneSignatory", signatoryUser.landlinePhone ?? "");
			setValue("phoneSignatory", signatoryUser.mobilePhone ?? "");
		}
		setValue("iban", societyData.brokerDetails?.iban || "");
		setValue("bic", societyData.brokerDetails?.bic || "");
		setValue("insurer", societyData.brokerDetails?.insurer || "");
		setValue("categories", categories);
		setValue("accessibleProducts", accessibleProducts);

		if (societyData.brokerDetails?.insurerValidityStartDate) {
			setValue(
				"validityStartDate",
				parseDate(
					format(
						new Date(societyData.brokerDetails.insurerValidityStartDate),
						"yyyy-MM-dd",
					),
				),
			);
		}

		if (societyData.brokerDetails?.insurerValidityEndDate) {
			setValue(
				"validityEndDate",
				parseDate(
					format(
						new Date(societyData.brokerDetails.insurerValidityEndDate),
						"yyyy-MM-dd",
					),
				),
			);
		}

		// Set documents with shouldValidate and shouldDirty to ensure updates
		setValue("companyDocuments", companyDocs, {
			shouldValidate: true,
			shouldDirty: false,
		});
		setValue("userDocuments", userDocs, {
			shouldValidate: true,
			shouldDirty: false,
		});
		setValue("lcbftDocuments", lcbDocs, {
			shouldValidate: true,
			shouldDirty: false,
		});
		setValue("rcDocuments", rcDocuments, {
			shouldValidate: true,
			shouldDirty: false,
		});
		setValue("bankDocuments", bankDocs, {
			shouldValidate: true,
			shouldDirty: false,
		});
	}, [societyData, setValue]);
	return {
		...form,
	};
}
